const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Adds a specified number of days to a given date.
 * If the date is invalid, returns null.
 *
 * @param {Date} date - Starting date
 * @param {number} days - Integer number of days to add to the date
 * @returns {Date|null} - New date with added days, or null if the input date is invalid
 */
export function addDaysToDate(date, days) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

/**
 * Whole days from now until a future date (ISO string, unix seconds, or Date).
 * Rounds up (a date later today → 1); negative once the date is in the past.
 * Returns null for an invalid/absent value.
 *
 * @param {string|number|Date} value
 * @returns {number|null}
 */
export function daysUntil(value) {
  const ms = toMs(value);
  if (ms === null) return null;
  return Math.ceil((ms - Date.now()) / MS_PER_DAY);
}

export function dateToUnixTimestamp(date) {
  return date ? Date.parse(date) / 1000 : null;
}

export function unixTimestampToDate(unixTimestamp) {
  return unixTimestamp ? new Date(unixTimestamp * 1000) : null;
}

export function dateToIsoDay(date) {
  if (!date) return null;
  return new Date(date).toISOString().slice(0, 10);
}

export function isoDayToDate(day) {
  if (!day) return null;
  return utcDayToLocalDate(isoDayToUtcDate(day));
}

// BaseDatepicker emits midday UTC and renders through `toInputDateString`,
// which reads the UTC day.
export function isoDayToInputDate(day) {
  if (!day) return null;
  const date = isoDayToUtcDate(day);
  date.setUTCHours(12);
  return date;
}

export function isoDayToUtcDate(day) {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, date));
}

export function toMs(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value * 1000;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function plusUtcDays(date, days) {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

export function utcDayToLocalDate(date) {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function expiryText(daysLeft, verb = "Expires") {
  if (daysLeft <= 0) return "Expired";
  return `${verb} ${daysLeft === 1 ? "today" : `in ${daysLeft} days`}`;
}
